import React from 'react';

import { CardProps } from '@components/card';
import { FormLabel } from '@components/form-label';
import { useDeleteModal } from '@components/generic-modals';
import { IconButton } from '@components/icon-button';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconEdit } from '@components/icons/edit';
import { IconTrash } from '@components/icons/trash';
import { Spinner } from '@components/loading-spinner';
import { Select } from '@components/select';
import { Separator } from '@components/separator';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import {
  useJourneyIntegrationEndpoints,
  useJourneyIntegrationWebhook,
} from 'api/integrations/hooks';
import {
  useCreateTravellerActionRelationship,
  useJourneyTravellerAction,
  useRemoveTravellerActionRelationship,
  useTravellerActionRelationships,
  useTravellerJourneyActions,
} from 'api/traveller-actions/hooks';
import { TravellerJourneyAction } from 'api/types';

import {
  getRelationshipOptions,
  DirectLine,
  ignoreTypes,
  RelationshipLine,
  SmallActionCard,
} from './common';

import { ACTION_TYPES } from '../action-config';
import { SidebarProps, TravellerActionHeader, useEditingContext } from '../common';

const ACTION_TYPE = ACTION_TYPES.journey_merge;

type RelationshipAction = {
  relationshipShortId: string | null;
  actionData: TravellerJourneyAction;
};

const relationshipActionStatusMap: {
  [key in TravellerJourneyAction['status']]: CardProps['variant'];
} = {
  completed: 'green',
  in_progress: 'orange',
  waiting_to_be_started: 'indigo',
  halted: 'green',
  upcoming: 'default',
  skipped: 'gray',
};

const findParentAction = (
  actions: TravellerJourneyAction[],
  shortId: string | undefined
): TravellerJourneyAction | undefined => {
  if (!shortId) return undefined;

  const action = actions.find((a) => a.shortId === shortId);
  if (!action || !['journey_branch', 'branch_base'].includes(action.action)) return action;

  return findParentAction(actions, action.parentAction);
};

export const SidebarContent = ({ shortId }: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const showDeleteModal = useDeleteModal();

  const addMutation = useCreateTravellerActionRelationship();
  const removeMutation = useRemoveTravellerActionRelationship();
  const { isEditing, startEditing, stopEditing } = useEditingContext();

  const actionQuery = useJourneyTravellerAction<typeof ACTION_TYPE>(shortId);
  const relationshipsQuery = useTravellerActionRelationships(shortId);
  const travellerActionsQuery = useTravellerJourneyActions(
    actionQuery.data?.travellerJourneyId ?? ''
  );
  const integrationsQuery = useJourneyIntegrationEndpoints(actionQuery.data?.journeyId ?? '');
  const webhooksQuery = useJourneyIntegrationWebhook(actionQuery.data?.journeyId ?? '');

  const integrations = integrationsQuery.data ?? [];
  const webhooks = webhooksQuery.data ?? [];

  const action = actionQuery.data;

  if (!relationshipsQuery.data || !action || !travellerActionsQuery.data) {
    return <Spinner />;
  }

  const parentAction = findParentAction(travellerActionsQuery.data, action.parentAction);
  if (!parentAction) {
    return <Spinner />;
  }
  const relationshipActions: RelationshipAction[] = [];

  const relationshipShortIds = relationshipsQuery.data.actions.map(
    (relationship) => relationship.parentAction
  );

  relationshipsQuery.data.actions.forEach((relationship) => {
    const action = travellerActionsQuery.data.find(
      (travellerAction) => travellerAction.shortId === relationship.parentAction
    );
    if (action) {
      relationshipActions.push({
        relationshipShortId: relationship.shortId,
        actionData: action,
      });
    }
  });

  const validMergeNodes = travellerActionsQuery.data.filter(
    (travellerAction) =>
      !ignoreTypes.includes(travellerAction.action) &&
      travellerAction.shortId !== shortId &&
      !action.ancestors.includes(travellerAction.shortId) &&
      !action.descendants.includes(travellerAction.shortId) &&
      !relationshipShortIds.includes(travellerAction.shortId)
  );

  const handleSave = ({
    parentShortId,
    childShortId,
  }: {
    parentShortId: string;
    childShortId: string;
  }) => {
    return addMutation.mutateAsync({ parentShortId, childShortId });
  };

  const relationshipOptions = getRelationshipOptions(
    validMergeNodes,
    integrations,
    webhooks,
    t('noDescription')
  );

  const canEdit = action.status !== 'completed';
  const isEditable = isEditing && canEdit;

  return (
    <Sidebar>
      <TravellerActionHeader actionType={ACTION_TYPE} />
      <Sidebar.Content>
        <Stack space={6}>
          <Stack space={2}>
            <FormLabel text={t('previous')} helpText={t('previousHelpText')} />
            <DirectLine />
            <SmallActionCard
              key={parentAction.shortId}
              actionData={parentAction}
              integrations={integrations}
              webhooks={webhooks}
              description={parentAction.description || t('noDescription')}
              variant={relationshipActionStatusMap[parentAction.status]}
            />
          </Stack>

          <Stack space={4} orientation="horizontal" style={{ width: '100%' }} align="center">
            <Separator />
            <Text font="semibold14" color="textHelper" text={t('core.and')} />
            <Separator />
          </Stack>

          <Stack space={4}>
            <Text text={t('selectedActions')} font="semibold14" />
            <RelationshipLine />
            <Stack space={2}>
              {relationshipActions.map((action) => (
                <SmallActionCard
                  key={action.relationshipShortId}
                  actionData={action.actionData}
                  integrations={integrations}
                  webhooks={webhooks}
                  description={action.actionData.description || t('noDescription')}
                  variant={relationshipActionStatusMap[action.actionData.status]}
                  onRemove={
                    isEditable && action.relationshipShortId
                      ? () =>
                          showDeleteModal({
                            title: t('deleteRelationship'),
                            description: t('deleteRelationshipDescription'),
                            buttonText: t('delete'),
                            hideCannotBeUndone: true,
                            onConfirm: () =>
                              removeMutation.mutateAsync(action.relationshipShortId!),
                          })
                      : undefined
                  }
                  removeIcon={<IconTrash />}
                />
              ))}
            </Stack>
            <Text text={t('selectedActionsNeeds')} font="regular14" />
            {isEditable && (
              <Select
                noOptions={() => t('noRelationships')}
                label={t('addRelationship')}
                options={relationshipOptions}
                onChange={(option) =>
                  option && handleSave({ childShortId: shortId, parentShortId: option })
                }
              />
            )}

            {canEdit && (
              <Sidebar.Footer>
                <IconButton
                  variant="ghost-purple"
                  icon={isEditing ? <IconArrowLeft /> : <IconEdit />}
                  onClick={isEditing ? stopEditing : startEditing}
                />
              </Sidebar.Footer>
            )}
          </Stack>
        </Stack>
      </Sidebar.Content>
    </Sidebar>
  );
};

const sidebarLocale = {
  en: {
    noRelationships: 'No available actions',
    previous: 'Previous action',
    previousNeeds: 'Needs to be completed or manually skipped',
    previousHelpText: '(In workflow pipeline)',
    noDescription: 'No description',
    selectedActions: 'Selected actions',
    selectedActionsNeeds: 'Need to be completed or manually / conditionally skipped',
    addRelationship: 'Add action',
    deleteRelationship: 'Delete condition',
    deleteRelationshipDescription:
      'Are you sure you want to delete this condition? The journey might continue if it is the last one remaning',
    delete: 'Delete',
  },
};
